import { PayloadAction, SerializedError } from '@reduxjs/toolkit';
import { TErrorResponse } from '~/store/types.ts';
import { errorMessages } from '~/constants/errorMessages.ts';
import {
  TAssetManagerDecodedToken,
  TClientLoginResponse,
  TClientUser,
  TManagerDecodedToken,
  TManagerLoginResponse,
  TUserRole,
  TUsersStore,
} from '~/store/users/types.ts';
import type { TApiSupervisorInfo } from '~/modules/covenants/types.ts';
import { encryptCookieValue } from '~/helpers/cookieCrypto';
import {
  IS_AUTHENTICATED_KEY,
  MANAGER_USERNAME_COOKIE,
  USERNAME_COOKIE,
} from '~/modules/auth/constants';
import { jwtDecode } from 'jwt-decode';
import { ManagerRole } from '~/constants/clientTypes';
import { FetchStatus } from '~/store/covenants/types.ts';
import { usersState } from './initialState';
import { AxiosError } from 'axios';
import { resolveRefreshSession } from './session';

const resetSupervisorInfo = (state: TUsersStore) => {
  state.supervisorInfo.data = null;
  state.supervisorInfo.status = FetchStatus.Idle;
  state.supervisorInfo.error = null;

  if (state.user.rights?.covenants) {
    delete state.user.rights.covenants;
  }
};

export const usersPending = (state: TUsersStore) => {
  state.loading = true;
  state.error = null;
};

export const usersFulfilled = (state: TUsersStore) => {
  state.loading = false;
  state.error = null;
};

export const registerUsersFulfilled = (state: TUsersStore, action: PayloadAction<TClientUser>) => {
  state.loading = false;
  state.error = null;
  encryptCookieValue(USERNAME_COOKIE, action.payload.username || '');
};

export const clientLoginFulfilled = (
  state: TUsersStore,
  action: PayloadAction<TClientLoginResponse>
) => {
  state.loading = false;
  state.isClientLogin = true;
  state.isManagerLogin = false;
  state.isInitialized = true;
  state.token = action.payload.token;
  state.user = action.payload.customerDTO;
  resetSupervisorInfo(state);
  encryptCookieValue(USERNAME_COOKIE, action.payload.customerDTO.username || '');
  localStorage.setItem(IS_AUTHENTICATED_KEY, 'true');
};

export const logoutFulfilled = () => {
  localStorage.removeItem(IS_AUTHENTICATED_KEY);
  return {
    ...usersState,
  };
};

export const parseUserByToken = (state: TUsersStore) => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (!token) {
      throw new Error('Токен не знайдено в URL');
    }

    const decodedToken = jwtDecode(token) as TAssetManagerDecodedToken;

    state.token = token;
    state.user = {
      ...(state.user as TClientUser),
      phone: decodedToken.phone,
      roles: decodedToken.roles as TUserRole,
      tokenData: decodedToken,
    };
  } catch (error: unknown) {
    state.error = (error as AxiosError).message || errorMessages.SERVICE_UNAVAILABLE;
  }
};

export const managerLoginFulfilled = (
  state: TUsersStore,
  action: PayloadAction<TManagerLoginResponse>
) => {
  state.loading = false;
  state.token = action.payload.token;
  state.isManagerLogin = true;
  state.isClientLogin = false;
  state.isInitialized = true;
  const decryptedToken: TManagerDecodedToken = jwtDecode(action.payload.token);
  const managerRolesFromToken = (decryptedToken?.activeDirectoryScopes || []) as ManagerRole[];
  const managerRolesFromResponse: ManagerRole[] = Array.isArray(action.payload.groups)
    ? action.payload.groups
    : [];

  (state.user as TClientUser).username = decryptedToken?.sub;
  (state.user as TClientUser).email = decryptedToken?.email;
  ((state.user as TClientUser).roles as ManagerRole[]) =
    managerRolesFromResponse.length > 0 ? managerRolesFromResponse : managerRolesFromToken;
  resetSupervisorInfo(state);
  encryptCookieValue(MANAGER_USERNAME_COOKIE, decryptedToken?.sub || '');
  localStorage.setItem(IS_AUTHENTICATED_KEY, 'true');
};

export const refreshFulfilled = (state: TUsersStore, action: PayloadAction<string | null>) => {
  state.loading = false;
  state.error = null;
  state.token = action.payload || '';

  try {
    const decodedToken = jwtDecode<TManagerDecodedToken>(action.payload || '');
    const session = resolveRefreshSession(decodedToken);

    state.user.username = session.username;
    state.user.email = session.email;
    state.user.roles = session.roles;
    state.isClientLogin = session.isClientLogin;
    state.isManagerLogin = session.isManagerLogin;
    state.isAssetManager = false;
  } catch {
    state.user.roles = [] as unknown as TUserRole;
    state.isClientLogin = false;
    state.isManagerLogin = false;
  }
};

export const supervisorInfoFulfilled = (
  state: TUsersStore,
  action: PayloadAction<TApiSupervisorInfo>
) => {
  state.supervisorInfo.status = FetchStatus.Success;
  state.supervisorInfo.data = action.payload;
  state.supervisorInfo.error = null;
  state.user.rights = {
    ...(state.user.rights ?? {}),
    covenants: action.payload,
  };
};

export const usersError = (
  state: TUsersStore,
  action: { error: SerializedError; payload?: TErrorResponse | unknown }
) => {
  state.loading = false;

  if (action.error.name === 'AbortError') {
    state.error = null;
  } else {
    state.error = (action.payload as TErrorResponse)?.message || errorMessages.SERVICE_UNAVAILABLE;
  }
};
